import { useState } from 'react';
import { getTreatments } from '../../firebase/treatmentService';
import { getValidConsent } from '../../firebase/consentService';

const consentText = `
TREATMENT CONSENT & DISCLAIMER

I have read the above information and have given an accurate account of all questions. 
If I have any concerns, I will address them with my stylist before our session begins.

I give my stylist full permission to perform any chemical services discussed and agreed upon.
I have had the chance to ask any questions I may have.

I will not hold the establishment nor the stylist accountable for any liabilities that 
may result from this service.

I understand that my stylist will take every precaution to ensure the integrity of my 
hair and/or eliminate any risk involved with the chemical treatments.

DATA PROTECTION (POPIA)

By signing below, I consent to Muse & Mane collecting and storing my personal information 
including my name, contact details, medical history, hair/skin records, and treatment history.

I understand that:
- My information is necessary to provide safe and appropriate services
- My information will only be used for service delivery, treatment recommendations, 
  aftercare, and legal/insurance requirements
- I have the right to access, correct, or withdraw my data at any time
- My data will be stored securely and not shared with third parties without my consent

This consent is valid for 1 year from the date of signing.
`;

function PrintButton({ client, hairRecord, skinRecord }) {
  const [printing, setPrinting] = useState(false);

  const handlePrint = async () => {
    setPrinting(true);
    const treatments = await getTreatments(client.clientId);
    const hairConsent = await getValidConsent(client.clientId, 'hair');
    const skinConsent = await getValidConsent(client.clientId, 'skin');

    const printWindow = window.open('', '_blank');

    const field = (label, value) => `<div class="field"><span class="label">${label}:</span> <span class="value">${value || 'Not provided'}</span></div>`;

    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>${client.name} - Complete Client Records</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body {
            font-family: Arial, Helvetica, sans-serif;
            margin: 0;
            padding: 20px;
            font-size: 12pt;
            line-height: 1.4;
            background: white;
          }
          .logo {
            text-align: center;
            margin-bottom: 20px;
          }
          .logo img {
            height: 60px;
          }
          h1 {
            font-size: 24pt;
            color: #333;
            border-bottom: 2px solid #ccc;
            padding-bottom: 8px;
            margin-bottom: 20px;
          }
          h2 {
            font-size: 18pt;
            color: #555;
            margin-top: 20px;
            margin-bottom: 15px;
            border-bottom: 1px solid #ddd;
          }
          .client-info {
            background: #f5f5f5;
            padding: 15px;
            margin-bottom: 20px;
            border-radius: 8px;
          }
          .field {
            margin-bottom: 6px;
          }
          .label {
            font-weight: bold;
            display: inline-block;
            width: 200px;
          }
          .value {
            display: inline-block;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            margin: 15px 0;
          }
          th, td {
            border: 1px solid #ddd;
            padding: 8px;
            text-align: left;
          }
          th {
            background: #f2f2f2;
          }
          .signature-block {
            margin-top: 30px;
            display: flex;
            justify-content: space-between;
            border-top: 1px solid #ccc;
            padding-top: 20px;
          }
          .consent-text {
            background: #fafafa;
            padding: 15px;
            margin: 15px 0;
            white-space: pre-wrap;
            font-size: 10pt;
            border-left: 3px solid #999;
          }
          /* Keep sections together on a single page if possible */
          .keep-together {
            page-break-inside: avoid;
          }
          /* Ensure no awkward breaks inside headings or tables */
          h1, h2, table, .client-info, .consent-text, .signature-block {
            page-break-inside: avoid;
          }
          @media print {
            body {
              margin: 0;
              padding: 0;
            }
          }
        </style>
      </head>
      <body>
        <div class="logo">
          <img src="/images/logo-grey.png" alt="Muse & Mane" />
        </div>

        <div class="keep-together">
          <h1>${client.name} – Client Records</h1>
          <div class="client-info">
            ${field('Phone', client.phoneNumber)}
            ${field('Email', client.email)}
            ${field('Age', client.age)}
            ${field('Created', new Date(client.createdAt).toLocaleDateString())}
          </div>
        </div>

        ${hairRecord ? `
        <div class="keep-together">
          <h2>Hair Record</h2>
          ${field('Alopecia', hairRecord.hasAlopecia ? 'Yes' : 'No')}
          ${field('Pregnant (last 6 months)', hairRecord.pregnantLast6Months ? 'Yes' : 'No')}
          ${field('Psoriasis/Eczema', hairRecord.hasPsoriasisEczema ? 'Yes' : 'No')}
          ${field('Frequent Gym/Swim', hairRecord.frequentGymOrSwim ? 'Yes' : 'No')}
          ${field('Allergies', hairRecord.allergies)}
          ${field('Medications', hairRecord.medications)}
          ${field('Hair Type', hairRecord.hairType)}
          ${field('Texture', hairRecord.hairTexture)}
          ${field('Length', hairRecord.hairLength)}
          ${field('Hair Condition', hairRecord.hairCondition)}
          ${field('Scalp Condition', hairRecord.scalpCondition)}
          ${field('Diagnostic Test', hairRecord.diagnosticTest)}
        </div>
        ` : '<div class="keep-together"><p>No hair record on file.</p></div>'}

        ${skinRecord ? `
        <div class="keep-together">
          <h2>Skin Record</h2>
          ${field('Skin Description', skinRecord.skinDescription)}
          ${field('Skin Sensitivity', skinRecord.skinSensitivity)}
          ${field('Fitzpatrick Type', skinRecord.fitzpatrickType)}
          ${field('Skin Conditions', (skinRecord.skinConditions || []).join(', '))}
          ${field('Medications', skinRecord.skinMedications)}
          ${field('Allergies', skinRecord.skinAllergies)}
          ${field('Recent Treatments (6 weeks)', (skinRecord.recentTreatments || []).join(', '))}
          ${field('Pregnant/Lactating', skinRecord.pregnantOrLactating ? 'Yes' : 'No')}
          ${field('Skin Regime', skinRecord.skinRegime)}
          ${field('Products Used', skinRecord.productsUsed)}
        </div>
        ` : ''}

        <div class="keep-together">
          <h2>Treatment History</h2>
          ${treatments.length === 0 ? '<p>No treatments recorded.</p>' : `
          <table>
            <thead>
              <tr><th>Date</th><th>Type</th><th>Treatment</th><th>Staff</th><th>Samples Given</th><th>Product Bought</th></tr>
            </thead>
            <tbody>
              ${treatments.map(t => `
              <tr>
                <td>${new Date(t.date).toLocaleDateString()}</td>
                <td>${t.recordType === 'hair' ? 'Hair' : 'Skin'}</td>
                <td>${t.treatment}</td>
                <td>${t.staffName}</td>
                <td>${t.samplesGiven || '-'}</td>
                <td>${t.productBought || '-'}</td>
              </tr>
              `).join('')}
            </tbody>
          </table>
          `}
        </div>

        ${hairConsent && hairConsent.clientSignature ? `
        <div class="keep-together">
          <h2>Hair Service Consent</h2>
          <div class="consent-text">${consentText.replace(/\n/g, '<br>')}</div>
          <div class="signature-block">
            <div><strong>Client Signature:</strong> ${hairConsent.clientSignature}</div>
            <div><strong>Staff Name:</strong> ${hairConsent.staffName}</div>
            <div><strong>Staff Signature:</strong> ${hairConsent.staffSignature}</div>
          </div>
          <p><strong>Signed:</strong> ${new Date(hairConsent.signedAt).toLocaleString()}</p>
          <p><strong>Expires:</strong> ${new Date(hairConsent.expiresAt).toLocaleDateString()}</p>
        </div>
        ` : ''}

        ${skinConsent && skinConsent.clientSignature ? `
        <div class="keep-together">
          <h2>Skin Service Consent</h2>
          <div class="consent-text">${consentText.replace(/\n/g, '<br>')}</div>
          <div class="signature-block">
            <div><strong>Client Signature:</strong> ${skinConsent.clientSignature}</div>
            <div><strong>Therapist Name:</strong> ${skinConsent.staffName}</div>
            <div><strong>Therapist Signature:</strong> ${skinConsent.staffSignature}</div>
          </div>
          <p><strong>Signed:</strong> ${new Date(skinConsent.signedAt).toLocaleString()}</p>
          <p><strong>Expires:</strong> ${new Date(skinConsent.expiresAt).toLocaleDateString()}</p>
        </div>
        ` : ''}

        <div class="keep-together">
          <p style="text-align: center; font-size: 10pt; color: #999; margin-top: 50px;">Generated on ${new Date().toLocaleString()} | Muse & Mane Salon System</p>
        </div>
      </body>
      </html>
    `;

    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.print();
    setPrinting(false);
  };

  return (
    <button onClick={handlePrint} disabled={printing} className="px-3 py-1 bg-salon-button text-white rounded-md hover:bg-salon-button-hover text-sm disabled:opacity-50">
      {printing ? 'Loading...' : '🖨️ Print Records'}
    </button>
  );
}

export default PrintButton;